import React from 'react';
import { Group, Circle, Star, RegularPolygon, Text, Rect } from 'react-konva';
import { DecorItem as DecorItemType, Position, Size } from '../../types';

interface DecorItemProps {
  id: string;
  item: DecorItemType;
  position: Position;
  size: Size;
  rotation: number;
  isSelected: boolean;
  onDragStart: (e: any) => void;
  onDragEnd: (e: any) => void;
  onClick: (e: any) => void;
  onRotate: (id: string, rotation: number) => void;
}

const DecorItem: React.FC<DecorItemProps> = ({
  id,
  item,
  position,
  size,
  rotation,
  isSelected,
  onDragStart,
  onDragEnd,
  onClick,
  onRotate
}) => {
  const { decorType } = item;
  const radius = Math.min(size.width, size.height) / 2;

  const renderDecorShape = () => {
    switch (decorType) {
      case 'flower':
        return (
          <>
            <Circle
              id={id}
              radius={radius}
              fill="#f8c1d0"
              stroke={isSelected ? "#3498db" : "#e5a2b6"}
              strokeWidth={isSelected ? 2 : 1}
            />
            <Text
              id={id}
              text="🌸"
              fontSize={radius}
              align="center"
              verticalAlign="middle"
              offsetX={radius / 2}
              offsetY={radius / 2}
            />
          </>
        );
      case 'plant':
        return (
          <>
            <Circle
              id={id}
              radius={radius}
              fill="#a0d9a0"
              stroke={isSelected ? "#3498db" : "#80b380"}
              strokeWidth={isSelected ? 2 : 1}
            />
            <Text
              id={id}
              text="🪴"
              fontSize={radius}
              align="center"
              verticalAlign="middle"
              offsetX={radius / 2}
              offsetY={radius / 2}
            />
          </>
        );
      case 'candle':
        return (
          <>
            <RegularPolygon
              id={id}
              sides={6}
              radius={radius}
              fill="#fff5e6"
              stroke={isSelected ? "#3498db" : "#e6d9c2"}
              strokeWidth={isSelected ? 2 : 1}
            />
            <Text
              id={id}
              text="🕯️"
              fontSize={radius}
              align="center"
              verticalAlign="middle"
              offsetX={radius / 2}
              offsetY={radius / 2}
            />
          </>
        );
      case 'star':
        return (
          <Star
            id={id}
            numPoints={5}
            innerRadius={radius * 0.5}
            outerRadius={radius}
            fill="#f9d423"
            stroke={isSelected ? "#3498db" : "#e8c41f"}
            strokeWidth={isSelected ? 2 : 1}
          />
        );
      default:
        return (
          <Circle
            id={id}
            radius={radius}
            fill="#e0e0e0"
            stroke={isSelected ? "#3498db" : "#888"}
            strokeWidth={isSelected ? 2 : 1}
          />
        );
    }
  };

  // Handle rotation with the rotation handle
  const handleRotateStart = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
  };
  
  const handleRotateMove = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
    
    // Calculate the center of the item
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const groupPos = {
      x: position.x,
      y: position.y
    };
    
    // Calculate angle between center of item and current mouse position
    const angle = Math.atan2(
      pointerPos.y - groupPos.y,
      pointerPos.x - groupPos.x
    ) * 180 / Math.PI + 90;
    
    // Round to nearest 15 degrees for better usability
    const snappedAngle = Math.round(angle / 15) * 15;
    
    onRotate(id, snappedAngle);
  };

  return (
    <Group
      id={id}
      x={position.x}
      y={position.y}
      rotation={rotation}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {renderDecorShape()}

      {/* Rotation Handle - only show when selected */}
      {isSelected && (
        <>
          {/* Rotation indicator line */}
          <Rect
            x={0}
            y={-size.height / 2 - 30}
            width={2}
            height={30}
            fill="#3498db"
            offsetX={1}
          />
          
          {/* Rotation handle */}
          <Circle
            x={0}
            y={-size.height / 2 - 40}
            radius={10}
            fill="#3498db"
            stroke="#fff"
            strokeWidth={2}
            draggable
            onDragStart={handleRotateStart}
            onDragMove={handleRotateMove}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'grab';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
          />
        </>
      )}
    </Group>
  );
};

export default DecorItem; 